function getTimezoneOffset(date: Date): string {
  const offset = date.getTimezoneOffset();
  const sign = offset < 0 ? "+" : "-";
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;

  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0",
  )}`;
}

function formatDateTime(date: Date, format: string, timezone?: string): string {
  let year: number,
    month: number,
    day: number,
    hours: number,
    minutes: number,
    seconds: number,
    milliseconds: number;

  if (timezone) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? "0";
    year = parseInt(get("year"));
    month = parseInt(get("month"));
    day = parseInt(get("day"));
    hours = parseInt(get("hour"));
    if (hours === 24) hours = 0;
    minutes = parseInt(get("minute"));
    seconds = parseInt(get("second"));
    milliseconds = 0;
  } else {
    year = date.getFullYear();
    month = date.getMonth() + 1;
    day = date.getDate();
    hours = date.getHours();
    minutes = date.getMinutes();
    seconds = date.getSeconds();
    milliseconds = date.getMilliseconds();
  }

  const isPM = hours >= 12;
  const formattedHours = hours % 12 || 12;

  const formatMap: { [key: string]: string | number } = {
    yyyy: year,
    MM: String(month).padStart(2, "0"),
    dd: String(day).padStart(2, "0"),
    hh: String(hours).padStart(2, "0"),
    mm: String(minutes).padStart(2, "0"),
    ss: String(seconds).padStart(2, "0"),
    SSS: String(milliseconds).padStart(3, "0"),
    TZ: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    P: getTimezoneOffset(date),
    h: String(formattedHours).padStart(2, "0"),
    a: isPM ? "PM" : "AM",
  };

  let formattedDate = format;
  for (const key in formatMap) {
    formattedDate = formattedDate.replace(key, String(formatMap[key]));
  }

  return formattedDate;
}

export function formatDateTimeFromString(
  dateTimeString: string,
  format: string,
  timezone?: string,
): string {
  // Date-only strings (YYYY-MM-DD) are calendar dates, not points in time.
  // Parse directly to avoid timezone conversion shifting the date.
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeString)) {
    const [year, month, day] = dateTimeString.split("-");
    const formatMap: { [key: string]: string } = {
      yyyy: year,
      MM: month,
      dd: day,
    };
    let formattedDate = format;
    for (const key in formatMap) {
      formattedDate = formattedDate.replace(key, formatMap[key]);
    }
    return formattedDate;
  }

  const date = new Date(dateTimeString);
  return formatDateTime(date, format, timezone);
}

export function formatDateTimeFromDate(
  date: Date,
  format: string,
  timezone?: string,
): string {
  return formatDateTime(date, format, timezone);
}
